import { create } from "zustand"
import { useMutation } from "@apollo/client"
import { graphql } from "@/gql"
import { useCallback, useEffect, useState } from "react"
import { jwtDecode } from "jwt-decode"
import { useSession } from "./useSession"
import { Router } from "next/router"
import { useRouter } from "next-nprogress-bar"

const ObtainMessagingTokenMutation = graphql(`
  mutation ObtainMessagingToken {
    obtainChatToken {
      ok
      token
    }
  }
`)

type MessagingTokenStore = {
  messagingToken: string | null
  setMessagingToken: (messagingToken: string) => void
}

export const useChatCountsStore = create<MessagingTokenStore>((set, get) => ({
  messagingToken: null,
  setMessagingToken: (messagingToken: string) => {
    set(() => ({ messagingToken }))
  },
}))

const maxRetries = 3 // Set max retry attempts
const retryDelay = 2000 // Delay between retries (2 seconds)
const refreshThreshold = 2 * 60 * 60 // 2 hours in seconds

export const useMessagingToken = () => {
  const { data: sessionData, update: updateSession } = useSession()
  const { messagingToken, setMessagingToken } = useChatCountsStore()
  const [retryCount, setRetryCount] = useState(0) // Track retry attempts

  const router = useRouter()

  const [getMessagingToken, { loading }] = useMutation(ObtainMessagingTokenMutation, {
    onCompleted(data) {
      if (data)
        if (data?.obtainChatToken?.ok && data?.obtainChatToken?.token) {
          setMessagingToken(data.obtainChatToken.token)
          setRetryCount(0)
        } else {
          retry()
        }
    },
    onError(error) {
      if (error.message === "You are currently suspended.") {
        updateSession({ ...sessionData, user: { ...sessionData?.user, isSuspended: true } }).then(() => {
          router.push("/suspended")
        })
        return
      }
      retry()
    },
  })

  const retry = useCallback(() => {
    if (sessionData?.user.isSuspended) {
      return
    }
    if (retryCount < maxRetries) {
      setTimeout(
        () => {
          setRetryCount(prevRetryCount => prevRetryCount + 1)
          getMessagingToken()
        },
        retryDelay * (retryCount + 1)
      ) // Exponential backoff
    }
  }, [retryCount, getMessagingToken])

  const decodeToken = (token: string) => {
    try {
      return jwtDecode<{ exp: number }>(token)
    } catch (error) {
      console.error("Failed to decode JWT", error)
      return null
    }
  }

  const isTokenExpiringSoon = useCallback((token: string) => {
    const decoded = decodeToken(token)
    if (!decoded) return false

    const currentTime = Math.floor(Date.now() / 1000) // Current time in seconds
    const timeUntilExpiration = decoded.exp - currentTime

    return timeUntilExpiration <= refreshThreshold
  }, [])

  const fetchToken = useCallback(() => {
    if (sessionData?.user.isSuspended) {
      return
    }
    if (!messagingToken && !loading) {
      getMessagingToken()
    } else if (messagingToken && isTokenExpiringSoon(messagingToken) && !loading) {
      getMessagingToken()
    }
  }, [getMessagingToken, messagingToken, loading, isTokenExpiringSoon])

  useEffect(() => {
    fetchToken()
  }, [fetchToken])

  return messagingToken
}
